import React, { useState, useEffect } from 'react';
import {
  Landmark,
  ShieldCheck,
  TrendingUp,
  FileSpreadsheet,
  Download,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  FileText,
  PieChart,
  Calculator,
  ArrowRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  coreInsuranceApi,
  MasterRbmQuarterlyReturnDto
} from '../api/coreInsuranceApi';

export default function CoreRegulatory() {
  const [report, setReport] = useState<MasterRbmQuarterlyReturnDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'form1' | 'form2' | 'form3' | 'ifrs17'>('form1');

  // Controls
  const [year, setYear] = useState(2026);
  const [quarter, setQuarter] = useState(2);
  const [capital, setCapital] = useState(2500000000); // MWK 2.5 Billion

  // UPR Runoff Schedule
  const [uprSchedule, setUprSchedule] = useState<any | null>(null);
  const [loadingUpr, setLoadingUpr] = useState(false);

  useEffect(() => {
    fetchMasterReturn();
  }, [year, quarter]);

  const fetchMasterReturn = async () => {
    setLoading(true);
    try {
      const data = await coreInsuranceApi.getMasterQuarterlyReturn(year, quarter, capital);
      setReport(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load RBM quarterly returns.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUprSchedule = async () => {
    setLoadingUpr(true);
    try {
      const data = await coreInsuranceApi.getUprRunoffSchedule();
      setUprSchedule(data);
    } catch (err) {
      toast.error('Failed to load UPR schedule.');
    } finally {
      setLoadingUpr(false);
    }
  };

  const handleExportJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `RBM_Quarterly_Return_${year}_Q${quarter}_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('RBM statutory JSON filing export downloaded!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Landmark className="text-amber-600 dark:text-amber-400" />
            RBM Statutory Regulatory Returns & Solvency II / IFRS 17
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Reserve Bank of Malawi Insurance Directorate statutory returns, 1/365th UPR, OSLR, IBNR, and Solvency Margin.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportJson}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition text-xs"
          >
            <Download size={16} />
            Export Statutory JSON
          </button>
          <button
            onClick={fetchMasterReturn}
            className="p-2 bg-white dark:bg-slate-800 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Control Filters */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 text-xs">
          <div>
            <label className="block font-medium text-gray-600 dark:text-gray-400 mb-1">Return Year</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-bold"
            >
              <option value={2026}>2026</option>
              <option value={2025}>2025</option>
            </select>
          </div>

          <div>
            <label className="block font-medium text-gray-600 dark:text-gray-400 mb-1">Quarter</label>
            <select
              value={quarter}
              onChange={(e) => setQuarter(parseInt(e.target.value))}
              className="p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-bold"
            >
              <option value={1}>Q1 (Ending 31 Mar)</option>
              <option value={2}>Q2 (Ending 30 Jun)</option>
              <option value={3}>Q3 (Ending 30 Sep)</option>
              <option value={4}>Q4 (Ending 31 Dec)</option>
            </select>
          </div>

          <div>
            <label className="block font-medium text-gray-600 dark:text-gray-400 mb-1">Admissible Capital (MWK)</label>
            <input
              type="number"
              value={capital}
              onChange={(e) => setCapital(parseFloat(e.target.value))}
              className="p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono font-bold w-48"
            />
          </div>

          <button
            onClick={fetchMasterReturn}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-sm"
          >
            Recalculate
          </button>
        </div>

        <div className="text-right text-xs text-gray-500">
          <div>Licensed Insurer: <span className="font-semibold text-gray-900 dark:text-white">Xtrem Insure General Malawi</span></div>
          <div>License: <span className="font-mono text-amber-600 font-bold">RBM/INS/GEN/2026/012</span></div>
        </div>
      </div>

      {/* KPI Cards & Solvency Status */}
      {report && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
            <span className="text-xs font-semibold text-gray-400 uppercase">RBM Solvency Ratio</span>
            <div className={`text-2xl font-bold mt-1 ${
              report.solvencyMarginForm3.isCompliantWithRbmStandards ? 'text-emerald-600' : 'text-red-600'
            }`}>
              {report.solvencyMarginForm3.solvencyRatioPercentage.toFixed(1)}%
            </div>
            <span className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-1">
              <CheckCircle size={13} /> Compliant (Min. 100%)
            </span>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
            <span className="text-xs font-semibold text-gray-400 uppercase">Required Solvency Margin (RSM)</span>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              MWK {(report.solvencyMarginForm3.requiredSolvencyMargin / 1000000).toFixed(0)}M
            </div>
            <span className="text-xs text-gray-500 font-medium">Statutory Capital Floor</span>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
            <span className="text-xs font-semibold text-gray-400 uppercase">Capital Surplus / Buffer</span>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
              MWK {(report.solvencyMarginForm3.solvencySurplusOrDeficit / 1000000).toFixed(0)}M
            </div>
            <span className="text-xs text-blue-500 font-medium">Excess admissible buffer</span>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
            <span className="text-xs font-semibold text-gray-400 uppercase">IFRS 17 Service Result</span>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
              MWK {(report.ifrs17InsuranceServiceResult / 1000000).toFixed(2)}M
            </div>
            <span className="text-xs text-purple-500 font-medium">Underwriting Insurance Margin</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-slate-800 flex gap-4 text-sm font-semibold">
        <button
          onClick={() => setActiveTab('form1')}
          className={`pb-3 px-2 flex items-center gap-2 border-b-2 transition ${
            activeTab === 'form1'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText size={16} />
          Form 1: Revenue Account
        </button>

        <button
          onClick={() => {
            setActiveTab('form2');
            if (!uprSchedule) fetchUprSchedule();
          }}
          className={`pb-3 px-2 flex items-center gap-2 border-b-2 transition ${
            activeTab === 'form2'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calculator size={16} />
          Form 2: Technical Provisions (365ths UPR & OSLR)
        </button>

        <button
          onClick={() => setActiveTab('form3')}
          className={`pb-3 px-2 flex items-center gap-2 border-b-2 transition ${
            activeTab === 'form3'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <ShieldCheck size={16} />
          Form 3: Solvency Margin & Capital Adequacy
        </button>
      </div>

      {/* TAB 1: FORM 1 REVENUE ACCOUNT */}
      {activeTab === 'form1' && report && (
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="font-bold text-base text-gray-900 dark:text-white">
                RBM Form 1 — Revenue Account by Line of Business
              </h3>
              <p className="text-xs text-gray-500">Statutory return for the quarter ended in Year {year}</p>
            </div>
            <span className="font-mono text-xs px-2.5 py-1 bg-amber-100 text-amber-800 font-bold rounded">
              Currency: MWK
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-gray-300 font-semibold border-b">
                <tr>
                  <th className="py-2.5 px-3">Revenue / Expense Line Item</th>
                  <th className="py-2.5 px-3 text-right">Motor</th>
                  <th className="py-2.5 px-3 text-right">Property / Fire</th>
                  <th className="py-2.5 px-3 text-right font-bold text-blue-600">Total Aggregate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                <tr>
                  <td className="py-2 px-3 font-medium">Gross Written Premium (GWP)</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {report.revenueAccountForm1.totalAggregate.grossWrittenPremium.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono font-bold">
                    {report.revenueAccountForm1.totalAggregate.grossWrittenPremium.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-gray-500 pl-6">Less: Reinsurance Premium Ceded</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">
                    -{report.revenueAccountForm1.totalAggregate.reinsuranceCededPremium.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600 font-semibold">
                    -{report.revenueAccountForm1.totalAggregate.reinsuranceCededPremium.toLocaleString()}
                  </td>
                </tr>
                <tr className="bg-gray-50/50 dark:bg-slate-800/20 font-semibold">
                  <td className="py-2 px-3 text-gray-800 dark:text-gray-200">Net Written Premium (NWP)</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {report.revenueAccountForm1.totalAggregate.netWrittenPremium.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono text-blue-600">
                    {report.revenueAccountForm1.totalAggregate.netWrittenPremium.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-gray-500 pl-6">Less: Change in Unearned Premium Reserve (UPR)</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">
                    -{report.revenueAccountForm1.totalAggregate.changeInUpr.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">
                    -{report.revenueAccountForm1.totalAggregate.changeInUpr.toLocaleString()}
                  </td>
                </tr>
                <tr className="bg-blue-50/50 dark:bg-blue-950/20 font-bold">
                  <td className="py-2.5 px-3 text-blue-900 dark:text-blue-300">Net Earned Premium (NEP)</td>
                  <td className="py-2.5 px-3 text-right font-mono">
                    {report.revenueAccountForm1.totalAggregate.netEarnedPremium.toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono">0</td>
                  <td className="py-2.5 px-3 text-right font-mono text-blue-700 dark:text-blue-400">
                    {report.revenueAccountForm1.totalAggregate.netEarnedPremium.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-medium">Claims Paid (Net of Recoveries)</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">
                    {report.revenueAccountForm1.totalAggregate.claimsPaidNet.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">
                    {report.revenueAccountForm1.totalAggregate.claimsPaidNet.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-3 text-gray-500 pl-6">Add: Change in OSLR & IBNR Reserve</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {(report.revenueAccountForm1.totalAggregate.changeInOslr + report.revenueAccountForm1.totalAggregate.ibnrReserve).toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {(report.revenueAccountForm1.totalAggregate.changeInOslr + report.revenueAccountForm1.totalAggregate.ibnrReserve).toLocaleString()}
                  </td>
                </tr>
                <tr className="bg-red-50/40 dark:bg-red-950/20 font-semibold">
                  <td className="py-2 px-3 text-red-900 dark:text-red-300">Total Net Incurred Claims</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">
                    {report.revenueAccountForm1.totalAggregate.netIncurredClaims.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">
                    {report.revenueAccountForm1.totalAggregate.netIncurredClaims.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-medium">Net Commission Expense (Gross less Ceding)</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {report.revenueAccountForm1.totalAggregate.netCommissionExpense.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {report.revenueAccountForm1.totalAggregate.netCommissionExpense.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-medium">Management Expenses Apportioned</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {report.revenueAccountForm1.totalAggregate.managementExpenses.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">0</td>
                  <td className="py-2 px-3 text-right font-mono">
                    {report.revenueAccountForm1.totalAggregate.managementExpenses.toLocaleString()}
                  </td>
                </tr>
                <tr className="bg-emerald-50 dark:bg-emerald-950/30 border-t-2 border-emerald-500 font-extrabold text-sm">
                  <td className="py-3 px-3 text-emerald-900 dark:text-emerald-300">
                    Underwriting Profit / (Loss)
                  </td>
                  <td className="py-3 px-3 text-right font-mono text-emerald-700 dark:text-emerald-400">
                    MWK {report.revenueAccountForm1.totalAggregate.underwritingResultProfitOrLoss.toLocaleString()}
                  </td>
                  <td className="py-3 px-3 text-right font-mono">0</td>
                  <td className="py-3 px-3 text-right font-mono text-emerald-700 dark:text-emerald-400">
                    MWK {report.revenueAccountForm1.totalAggregate.underwritingResultProfitOrLoss.toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: FORM 2 TECHNICAL PROVISIONS */}
      {activeTab === 'form2' && report && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="font-bold text-base text-gray-900 dark:text-white">
              RBM Form 2 — Technical Provisions Summary
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs">
              <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-xl border">
                <span className="text-gray-500 font-medium">Unearned Premium Reserve (UPR)</span>
                <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                  MWK {report.technicalProvisionsForm2.totalUnearnedPremiumReserveUpr.toLocaleString()}
                </div>
                <span className="text-gray-400 text-[11px]">1/365th Pro-Rata Day Count</span>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-xl border">
                <span className="text-gray-500 font-medium">Outstanding Claims (OSLR)</span>
                <div className="text-lg font-bold text-amber-600 mt-1">
                  MWK {report.technicalProvisionsForm2.totalOutstandingClaimsReserveOslr.toLocaleString()}
                </div>
                <span className="text-gray-400 text-[11px]">{report.technicalProvisionsForm2.openClaimsEvaluatedCount} Open Case Estimates</span>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-xl border">
                <span className="text-gray-500 font-medium">Incurred But Not Reported (IBNR)</span>
                <div className="text-lg font-bold text-purple-600 mt-1">
                  MWK {report.technicalProvisionsForm2.totalIncurredButNotReportedIbnr.toLocaleString()}
                </div>
                <span className="text-gray-400 text-[11px]">Actuarial 7% NWP Model</span>
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-xl border border-blue-200 dark:border-blue-900">
                <span className="text-blue-800 dark:text-blue-300 font-bold">Total Technical Reserves</span>
                <div className="text-lg font-extrabold text-blue-700 dark:text-blue-400 mt-1">
                  MWK {report.technicalProvisionsForm2.totalTechnicalProvisions.toLocaleString()}
                </div>
                <span className="text-blue-600 text-[11px]">Mandatory Statutory Liability</span>
              </div>
            </div>
          </div>

          {/* UPR 365th RUNOFF SCHEDULE */}
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h4 className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
              <Calculator size={16} className="text-blue-600" />
              Granular 1/365th Day-Count Policy UPR Schedule
            </h4>

            {loadingUpr ? (
              <div className="py-4 text-center text-gray-400 text-xs">Computing 365th day-count factors...</div>
            ) : uprSchedule ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-800 text-gray-600 font-semibold border-b">
                    <tr>
                      <th className="py-2 px-3">Policy #</th>
                      <th className="py-2 px-3">Term Period</th>
                      <th className="py-2 px-3">Days Unexpired</th>
                      <th className="py-2 px-3">Unearned Factor</th>
                      <th className="py-2 px-3 text-right">Gross Written (MWK)</th>
                      <th className="py-2 px-3 text-right font-bold text-blue-600">Net UPR (MWK)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {uprSchedule.policies?.map((u: any) => (
                      <tr key={u.policyId}>
                        <td className="py-2 px-3 font-mono font-semibold">{u.policyNumber}</td>
                        <td className="py-2 px-3 text-gray-500">
                          {new Date(u.inceptionDate).toLocaleDateString()} - {new Date(u.expiryDate).toLocaleDateString()}
                        </td>
                        <td className="py-2 px-3 font-bold">{u.unexpiredDaysRemaining} / {u.totalTermDays}</td>
                        <td className="py-2 px-3 font-mono">{(u.unearnedProRataFactor * 100).toFixed(2)}%</td>
                        <td className="py-2 px-3 text-right font-mono">{u.grossWrittenPremium.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-blue-600">{u.unearnedPremiumReserveNet.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* TAB 3: FORM 3 SOLVENCY MARGIN */}
      {activeTab === 'form3' && report && (
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-6">
          <div className="border-b pb-3">
            <h3 className="font-bold text-base text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="text-emerald-600" />
              RBM Form 3 — Solvency Margin & Capital Adequacy Return
            </h3>
            <p className="text-xs text-gray-500">Under the Financial Services Act and Insurance Act regulations</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl border space-y-1">
              <span className="text-gray-500 font-semibold">1. Premium Index Method</span>
              <div className="text-base font-bold font-mono">
                MWK {report.solvencyMarginForm3.premiumIndexSolvencyRequirement.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-400">20% of Net Written Premium</span>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl border space-y-1">
              <span className="text-gray-500 font-semibold">2. Claims Index Method</span>
              <div className="text-base font-bold font-mono">
                MWK {report.solvencyMarginForm3.claimsIndexSolvencyRequirement.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-400">30% of Net Incurred Claims</span>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-xl border space-y-1">
              <span className="text-gray-500 font-semibold">3. Statutory Minimum Capital</span>
              <div className="text-base font-bold font-mono text-amber-600">
                MWK {report.solvencyMarginForm3.statutoryMinimumCapitalRequirement.toLocaleString()}
              </div>
              <span className="text-[11px] text-gray-400">Statutory Non-Life Insurer Floor</span>
            </div>
          </div>

          <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-2xl flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-emerald-950 dark:text-emerald-200">
                Required Solvency Margin (RSM): MWK {report.solvencyMarginForm3.requiredSolvencyMargin.toLocaleString()}
              </div>
              <div className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">
                {report.solvencyMarginForm3.regulatoryComplianceAssessment}
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs text-emerald-800 font-bold">Capital Adequacy Ratio</span>
              <div className="text-2xl font-extrabold text-emerald-600">
                {report.solvencyMarginForm3.solvencyRatioPercentage.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
